import React, { useCallback, useRef } from 'react';
import { Breadcrumb } from '@shared/components/breadcrumb/Breadcrumb';
import { getPageTitle } from '@shared/services/pageTitleService';
import { canUserAccessSeveralOrganization } from '@shared/services/userAccessibilityService';
import { Page } from '../../../layout';
import CheckpointForm from '../common/CheckpointForm';
import { PAGE_VIEW_MODES } from '../../../../constants/page-view-modes';
import { newLine } from '../../../../constants/textConstants';
import PrivilegeNames from '../../../../constants/privilegeNames';
import ApolloError from '@shared/components/ApolloError';
import useInterval from '../../hooks/useInterval';
import CheckPointMapper from '../mappers/checkPointMapper';
import { useReactiveVar } from '@apollo/client';
import { checkPointData } from '../../checkPointsCache';
import useSaveCheckPoint from '../../hooks/useSaveCheckPoint';
import { useNavigate } from 'react-router-dom';
import _ from 'lodash';

export const AUTO_SAVE_ONE_MINUTE = 60000;

/**
 * @typedef {object} AddCheckpointPageProps
 * @property {number} orgUnitId
 * @property {number} appraisalId
 * @property {number} checkPointId
 * @property {string} [orgUnitName]
 * @property {string} [orgName]
 * @property {React.ReactNode} [renderedOrgUnit]
 * @property {boolean} orgUnitLoading
 * @property {boolean} checkPointLoading
 * @property {string} [tier]
 * @property {Array<*>} [privileges]
 */

/**
 * @param {AddCheckpointPageProps} props
 */
const AddCheckpointPage = props => {
  const {
    orgUnitId,
    appraisalId,
    orgUnitName,
    checkPointId,
    renderedOrgUnit,
    orgUnitLoading,
    checkPointLoading,
    tier,
    privileges,
    orgName
  } = props;

  const checkPointCache = useReactiveVar(checkPointData);
  const navigate = useNavigate();

  const lastSavedCheckpoint = useRef(null);

  const previousLink = `/check-points/${orgUnitId}`;
  const title = 'Add Check Points';
  const successfullyMessage = 'Check Point added successfully.';

  const description = addCheckPointDescription(privileges);
  const pageTitle = getPageTitle([orgUnitName, 'Add Check Point']);

  const commonItems = [renderedOrgUnit, `Appraisal ${appraisalId}`];

  const annotationItems = canUserAccessSeveralOrganization(tier)
    ? [orgName, ...commonItems]
    : commonItems;

  const breadcrumbPathItems = [
    { name: 'Check Points', url: previousLink },
    { name: 'Add Check Point', url: '' }
  ];

  const { saveCheckPoint, saveError, autoSaveInProgress } = useSaveCheckPoint();

  const saveForm = useCallback(
    async isAutoSave => {
      const data = CheckPointMapper.checkPointToApolloInput(checkPointCache, checkPointId);
      return await saveCheckPoint(data, isAutoSave);
    },
    [checkPointCache, checkPointId, saveCheckPoint]
  );

  const onAutoSaveForm = async () => {
    const updatedCheckpoint = CheckPointMapper.checkPointToApolloInput(
      checkPointCache,
      checkPointId
    );

    if (_.isEqual(updatedCheckpoint, lastSavedCheckpoint.current)) {
      return;
    }

    const { data } = await saveForm(true);
    checkPointData(
      CheckPointMapper.checkPointToCacheData(
        data.saveCheckPoint,
        appraisalId,
        orgUnitId,
        PAGE_VIEW_MODES.ADD
      )
    );

    lastSavedCheckpoint.current = updatedCheckpoint;
  };

  const { resetInterval } = useInterval(onAutoSaveForm, AUTO_SAVE_ONE_MINUTE);

  const handleFormSave = async () => {
    resetInterval();
    await saveForm(false);
    navigate(previousLink, {
      state: { checkPointModifiedSuccessfullyMessage: 'Check Point saved successfully.' }
    });
  };

  if (saveError) {
    return <ApolloError />;
  }

  return (
    <Page title={pageTitle} className='check-point-page'>
      <Breadcrumb
        pathItems={breadcrumbPathItems}
        annotationItems={annotationItems}
        loading={orgUnitLoading}
      />

      <CheckpointForm
        title={title}
        description={description}
        successfullyMessage={successfullyMessage}
        previousLink={previousLink}
        orgUnitId={orgUnitId}
        appraisalId={appraisalId}
        viewMode={PAGE_VIEW_MODES.ADD}
        loading={checkPointLoading}
        checkPointId={checkPointId}
        handleFormSave={handleFormSave}
        autoSaveInProgress={autoSaveInProgress}
        resetInterval={resetInterval}
      />
    </Page>
  );
};

const getSaveDescription = canUploadRequiredFiles => {
  return canUploadRequiredFiles
    ? 'Click the “Submit” button to send the data to the relevant VIP stakeholders (ISACA, FDA, and participant). ' +
        'Clicking the “Save” button will keep the check point as a draft that can be edited up to 3 months from the creation date. ' +
        'If the draft is not submitted within 3 months, then it will be deleted.'
    : 'Finally, click the “Submit” button to store the data. Clicking the “Save” button will keep the check point as a draft ' +
        'that can be edited up to 3 months from the creation date. If the draft is not submitted within 3 months, then it will be deleted.';
};

const addCheckPointDescription = privileges => {
  const canUploadRequiredFiles = privileges.includes(
    PrivilegeNames.CheckPoints_UploadRequiredFiles
  );

  const introDescription =
    'To add a new Check Point, first select a Data Collection Date ' +
    'which is representative of the end of the data collection period.';

  const fileUploadDescription =
    'Next, use the “Upload” text in the first table to add a Check Point Report that includes all relevant ' +
    'information from the template. If preparing for the next appraisal, also upload a Reappraisal Rationale ' +
    'which includes all relevant information from the template- this will be shared with FDA. ' +
    'If the incorrect document was uploaded in error, use the “Remove” text to delete it.';

  const metricDescription =
    'use the “Add” text in the second table to enter up to 25 key metrics that the site monitors to ' +
    'understand product quality and organizational performance. If metrics were previously added, the primary ' +
    'metric fields can edited within the table. The status will default to “Update”, but can be changed to “Skip” ' +
    'when the data was unable to be collected for a period, “N/A” when the data would not typically be collected ' +
    'in a period, and “Discontinue” when the data will no longer be collected. To access additional editable ' +
    'metric fields, select the “Edit” text.';

  const autosaveAndEditDescription =
    'Check points will autosave every minute. Check points can be edited after submission.';

  const parts = [introDescription];

  if (canUploadRequiredFiles) parts.push(fileUploadDescription);

  parts.push((canUploadRequiredFiles ? 'Finally, ' : 'Next, ') + metricDescription);

  parts.push(getSaveDescription(canUploadRequiredFiles));

  parts.push(autosaveAndEditDescription);

  return parts.join(newLine + ' ' + newLine);
};

export default AddCheckpointPage;
